<script lang="ts">
	import OnboardingStepContainer from './OnboardingStepContainer.svelte';
	import { Alert, Icon, Popover, Select, SelectCard, Text } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import LiquidSolidType from 'src/components/Distance/LiquidSolidType.svelte';
	import OccupancyType from 'src/components/Distance/OccupancyType.svelte';
	import { createEventDispatcher, onMount } from 'svelte';
	import DistanceUnit from 'src/components/DevicesSettings/SettingsGBLE/DistanceUnit.svelte';
	import GarageShutterType from 'src/components/Distance/GarageShutterType.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device;
	export let deviceData;

	const dispatch = createEventDispatcher();
	let previewReady = false;

	onMount(() => {
		let innerFrame;
		const outerFrame = requestAnimationFrame(() => {
			innerFrame = requestAnimationFrame(() => {
				previewReady = true;
			});
		});
		return () => {
			cancelAnimationFrame(outerFrame);
			if (innerFrame) cancelAnimationFrame(innerFrame);
		};
	});

	$: solidsOptions = [
		{
			title: $format('solid_type_generic', { default: 'Generic' }),
			value: 'Generic',
		},
		{
			title: $format('solid_type_pellets', { default: 'Pellets/Feed' }),
			value: 'Pellets/Feed',
		},
		{
			title: $format('solid_type_grain', { default: 'Grain' }),
			value: 'Grain',
		},
		{
			title: $format('solid_type_sand', { default: 'Salt/Sand' }),
			value: 'Salt/Sand',
		},
	];

	$: garageOptions = [
		{
			title: $format('garage_option_vertical', { default: 'Vertical' }),
			value: 'Vertical',
		},
		{
			title: $format('garage_option_horizontal_left_to_right', {
				default: 'Horizontal (left to right)',
			}),
			value: 'Horizontal (left to right)',
		},
		{
			title: $format('garage_option_horizontal_right_to_left', {
				default: 'Horizontal (right to left)',
			}),
			value: 'Horizontal (right to left)',
		},
	];

	$: {
		const typeKey = deviceData?.modeType === 'garage' ? 'garageType' : 'solidsType';
		const options = deviceData?.modeType === 'garage' ? garageOptions : solidsOptions;
		const current = deviceData[typeKey];
		const typeValue = typeof current === 'string' ? current : current?.value;
		const matchedOption = options?.find((option) => option.value === typeValue) || options?.[0];

		if (
			matchedOption &&
			(typeof current === 'string' ||
				current?.value !== matchedOption.value ||
				current?.title !== matchedOption.title)
		) {
			deviceData[typeKey] = matchedOption;
		}
	}

	$: alertHeading =
		deviceData?.modeType === 'garage'
			? $format('alert_heading_track_position', {
					default:
						"Track door and shutter position, and see when they're opening or closing, based on movement-triggered measurements.",
				})
			: deviceData?.modeType === 'occupancy'
				? $format('alert_info_heading_occupancy', {
						default: 'Detect a single parking spot as Occupied or Vacant.',
					})
				: deviceData?.modeType === 'solids'
					? $format('alert_info_heading_solids', {
							default:
								'Measure loose materials as % full and depth. Best for granular/bulk solids that settle smoothly.',
						})
					: $format('alert_info_heading_liquid', {
							default: 'Measure the height of a liquid surface to show % full and depth.',
						});
</script>

<OnboardingStepContainer>
	<svelte:fragment slot="top">
		<div class="mb-m">
			<DistanceUnit
				{device}
				title={$format('title_choose_distance_unit_new', {
					default: 'Choose the distance unit you want to measure in.',
				})}
			/>
		</div>
	</svelte:fragment>

	<svelte:fragment slot="text">
		<Text styleType="body-small" class="mb-m">
			{$format('text_mode_type', {
				default:
					"Choose what you're measuring. You can change this later in Application settings.",
			})}
		</Text>
	</svelte:fragment>

	<svelte:fragment slot="left">
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => dispatch('select', 'liquids')}
			title={$format('label_liquids', { default: 'Liquids' })}
			class="mb-s width-full"
			icon="fas fa-droplet"
			descriptiveText={$format('descriptive_text_liquids', {
				default: 'Examples: tanks, cisterns, sumps.',
			})}
			selected={deviceData.modeType === 'liquids'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => dispatch('select', 'solids')}
			title={$format('label_solids', { default: 'Solids' })}
			class="mb-s width-full"
			icon="fas fa-circle-half-stroke"
			descriptiveText={$format('descriptive_text_solids', {
				default: 'Examples: pellets, grain, sand.',
			})}
			selected={deviceData.modeType === 'solids'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => dispatch('select', 'occupancy')}
			title={$format('label_occupancy', { default: 'Occupancy' })}
			class="mb-s width-full"
			icon="fas fa-car"
			descriptiveText={$format('descriptive_text_occupancy', {
				default: 'Examples: parking spot, garage bay.',
			})}
			selected={deviceData.modeType === 'occupancy'}
		/>
		<SelectCard
			cardBgColor={$showNewLayout ? 'color-1' : undefined}
			type="horizontal"
			variant="radio"
			on:click={() => dispatch('select', 'garage')}
			title={$format('select_card_title_garage_shutters', { default: 'Garage doors & shutters' })}
			class="mb-s width-full"
			icon="fas fa-garage"
			descriptiveText={$format('select_card_descriptive_text_garage_examples', {
				default: 'Examples: single-panel/roller doors.',
			})}
			selected={deviceData.modeType === 'garage'}
		/>

		<Alert type="info" heading={alertHeading} />

		{#if deviceData.modeType === 'solids'}
			<div class="mt-m mb-s d-flex align-items-center">
				<Text styleType="h2" class="mr-s">
					{$format('solids_appearance_selector', {
						default: 'Solids appearance selector',
					})}
				</Text>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<Text slot="customContent" styleType="body-extra-small">
						{$format('popover_solids_appearance_selector', {
							default:
								'Changes only the UI meter texture. Measurements and functionality are unchanged.',
						})}
					</Text>
				</Popover>
			</div>
			<Select
				aria-label={$format('select_solids_options', {
					default: 'Select solids options',
				})}
				bind:value={deviceData.solidsType}
				icon="fas fa-circle-half-stroke"
				id="select-solids-options"
				options={solidsOptions}
			/>
		{:else if deviceData.modeType === 'garage'}
			<div class="mt-m mb-s d-flex align-items-center">
				<Text styleType="h2" class="mr-s">
					{$format('text_door_shutter_orientation', {
						default: 'Door/shutter orientation',
					})}
				</Text>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<Text slot="customContent" styleType="body-extra-small">
						{$format('text_info_popover_garage', {
							default:
								'Vertical for doors that move up/down, Horizontal for doors that slide sideways. Visual only - measurement works the same.',
						})}
					</Text>
				</Popover>
			</div>
			<Select
				aria-label={$format('select_garage_options', {
					default: 'Select garage options',
				})}
				bind:value={deviceData.garageType}
				icon="fas fa-garage"
				id="select-garage-options"
				options={garageOptions}
			/>
		{/if}
	</svelte:fragment>

	<div class="mode-preview" slot="right">
		{#if deviceData.modeType === 'garage'}
			<GarageShutterType {device} {deviceData} />
		{:else if deviceData.modeType === 'occupancy'}
			<div class="occupancy-container">
				<OccupancyType {device} />
			</div>
		{:else if previewReady}
			<div class="liquid-container">
				<LiquidSolidType {device} {deviceData} />
			</div>
		{/if}
	</div>
</OnboardingStepContainer>

<style>
	.mode-preview {
		width: 100%;
		min-height: 300px;
	}

	.liquid-container {
		display: flex;
		justify-content: end;
		margin-bottom: 0;
		min-height: 300px;
	}

	@media (max-width: 639.98px) {
		.liquid-container {
			justify-content: center;
			margin-bottom: 16px;
		}
	}
</style>
